// Effect 9 — Tile personality. A minority of letter tiles get character, in 4 rarity classes:
//   common   — plain (workhorse letters E A T S R…, so most tiles stay quiet)
//   uncommon — subdued per-letter colour skin, no face
//   rare     — colour skin + ASCII eyebrows that carry emotion
//   epic     — rare + can flirt / troll a fellow epic in the same word, as a timed REEL
//
// Rarity is fixed PER LETTER (identity, from core/rarity). Emotion is carried entirely by ASCII
// EYEBROWS — engaged (^^) · dim (--) · worried (vv) · directional (>> / <<) — no emoji. The flirt/
// troll plays as an initiator-driven timed reel (stand → present → fire → receive → sit) with
// per-beat durations from feel.json, and the fired thing is small love/troll PARTICLES.
import { feel } from "../feel";
import { rand, forkRand } from "../../core/rng";
import { registerEffect, effectEnabled } from "../registry";
import { bus } from "../../core/bus";
import { intensity } from "../../tuner/scale";
import { rarityOf, type Rarity } from "../../core/rarity";
import { reduceMotion } from "../a11y";

type Brow = "engaged" | "dim" | "worried" | "toward" | "away" | "wink";

// Big mode: max out expression + particles so it's obvious. Default ON (pairs with lively words);
// the v1.2 panel toggles it, funpanel/welcome assume it.
let bigMode = true;
export function setPersonalityBig(on: boolean): void {
  bigMode = on;
}

// Replay speed: every reel beat / hold / particle travel is divided by this. It's a REPLAY setting,
// not a persistent tunable — the ⟳ replay buttons set it for the word they replay (½× / 1× / 2×).
let activeSpeed = 1;
export function setReelSpeed(x: number): void {
  activeSpeed = Math.max(0.2, x);
}
function speed(): number {
  return activeSpeed;
}

// Showcase (left fun buttons): arm the NEXT word to GUARANTEE a reel with a chosen flavor —
// true = flirt, false = troll, null = random. Consumed on the next react().
let showcaseArmed = false;
let showcaseFlirt: boolean | null = null;
let showcaseMinRarity: Rarity = 3; // normally the reel needs two EPICS; welcome lowers it to rare
export function armShowcase(flirt: boolean | null, minRarity: Rarity = 3): void {
  showcaseArmed = true;
  showcaseFlirt = flirt;
  showcaseMinRarity = minRarity;
}

// ---- personality STYLE (spinner: eyebrows | limbs) ---------------------------------------------
// Eyebrows-only is the original look; "limbs" also gives rare/epic tiles little hands + feet that
// wave / tap. Adding a style = a new entry here + its dressing (and idle gestures that use it).
export type PStyle = "eyebrows" | "limbs";
export const PERSONALITY_STYLES: PStyle[] = ["eyebrows", "limbs"];
let pStyle: PStyle = "eyebrows";
export function personalityStyle(): PStyle {
  return pStyle;
}
export function setPersonalityStyle(s: PStyle): void {
  pStyle = s;
  // Re-dress whatever is currently on screen so the switch shows immediately.
  document
    .querySelectorAll<HTMLElement>(".utterance .tile.p-rare, .utterance .tile.p-epic")
    .forEach((t) => setLimbs(t, pStyle === "limbs"));
}

// Limbs are ASCII glyphs in the eyebrow palette — the two feet (`_/` + `\_`) sit at the tile's base
// and together read `_/\_`. A toe-tap bounces one foot up and down (CSS `p-foottap`). Arms were
// removed: a glyph out the side collided with the neighbouring tiles.
const LIMB_REST: Record<string, string> = {
  "limb-foot-l": "_/",
  "limb-foot-r": "\\_",
};

/** Add or remove the ASCII feet (the "limbs" style) on a rare/epic tile. */
function setLimbs(tile: HTMLElement, on: boolean): void {
  tile.querySelectorAll(".tile-limb").forEach((e) => e.remove());
  if (!on) return;
  for (const cls of Object.keys(LIMB_REST)) {
    const el = document.createElement("span");
    el.className = `tile-limb ${cls}`;
    el.textContent = LIMB_REST[cls];
    tile.appendChild(el);
  }
}

// ---- idle life: occasional silly gestures on resting tiles, at random intervals ----------------
// Delight, not a nervous tic — a tile only *sometimes* does something, and never right after a
// reaction. Uses its OWN rng stream so idle timing can't perturb the seeded gameplay randomness.
const idleRand = forkRand(0x1d1e11);
let idleStarted = false;
let lastActivity = 0; // performance.now() of the last reaction; idle stays quiet just after one

function flickBack(tile: HTMLElement, side: "left" | "right"): void {
  window.setTimeout(() => {
    if (document.body.contains(tile) && tile.querySelector(".tile-eyes")) setBrows(tile, "dim", side);
  }, 600 + idleRand() * 500);
}
function sway(tile: HTMLElement): void {
  tile.style.transition = "transform 260ms ease-in-out";
  tile.style.transform = `rotate(${(idleRand() < 0.5 ? -1 : 1) * 4}deg) translateY(-2px)`;
  window.setTimeout(() => {
    if (document.body.contains(tile)) tile.style.transform = "";
  }, 320);
}
// Toe-tap: leg glyph stays put, only the outer toe char lifts (`_` down → `-` up), swapped a couple
// of times then settled — the same glyph-swap trick the eyebrows use, so nothing uncannily slides.
const TOE_TAP: Record<string, { down: string; up: string }> = {
  "limb-foot-l": { down: "_/", up: "-/" },
  "limb-foot-r": { down: "\\_", up: "\\-" },
};
function tap(tile: HTMLElement): void {
  const cls = idleRand() < 0.5 ? "limb-foot-l" : "limb-foot-r";
  const foot = tile.querySelector<HTMLElement>("." + cls);
  if (!foot) return;
  const s = TOE_TAP[cls];
  const frames = [s.up, s.down, s.up, s.down]; // toe up/down a couple of times
  frames.forEach((g, i) =>
    window.setTimeout(() => {
      if (document.body.contains(foot)) foot.textContent = g;
    }, i * 130),
  );
  window.setTimeout(() => {
    if (document.body.contains(foot)) foot.textContent = s.down;
  }, frames.length * 130);
}
function idleGesture(tile: HTMLElement): void {
  const side: "left" | "right" = tile.dataset.pside === "right" ? "right" : "left";
  const rm = reduceMotion();
  const hasLimbs = !!tile.querySelector(".tile-limb");
  const pool: Array<() => void> = [
    () => { setBrows(tile, "engaged", side); flickBack(tile, side); }, // brow raise
    () => { setBrows(tile, "toward", side); flickBack(tile, side); }, // peek toward a neighbour
    () => { setBrows(tile, "wink", side); flickBack(tile, side); }, // cheeky wink
  ];
  if (!rm) pool.push(() => sway(tile));
  // In limbs mode, lean into the toe-taps (weighted so they show up often).
  if (!rm && hasLimbs) pool.push(() => tap(tile), () => tap(tile), () => tap(tile));
  pool[Math.floor(idleRand() * pool.length)]();
}
function idleTick(): void {
  scheduleIdle();
  if (!effectEnabled("personality")) return;
  if (performance.now() - lastActivity < 2200) return; // quiet right after a reaction
  const tiles = [
    ...document.querySelectorAll<HTMLElement>(".utterance .tile.p-rare, .utterance .tile.p-epic"),
  ];
  if (tiles.length && idleRand() < 0.7) idleGesture(tiles[Math.floor(idleRand() * tiles.length)]);
}
function scheduleIdle(): void {
  window.setTimeout(idleTick, 2600 + idleRand() * 4200); // 2.6–6.8s between checks
}
function startIdle(): void {
  if (idleStarted) return;
  idleStarted = true;
  scheduleIdle();
}

function hueOf(letter: string): number {
  return (letter.toLowerCase().charCodeAt(0) * 47) % 360;
}

// ---- ASCII eyebrows (carry every emotion) ------------------------------------------------------
function browGlyph(state: Brow, side: "left" | "right"): string {
  switch (state) {
    case "engaged": return "^^";
    case "worried": return "vv";
    case "wink": return side === "left" ? "^-" : "-^"; // one brow up, one down — toward the partner
    case "toward": return side === "left" ? ">>" : "<<";
    case "away": return side === "left" ? "<<" : ">>";
    case "dim":
    default: return "--";
  }
}

function setBrows(tile: HTMLElement, state: Brow, side: "left" | "right", accent?: "flirt" | "troll"): void {
  const cls = accent ? `p-ascii-${accent}` : "";
  const span = `<span class="tile-eyes p-ascii ${cls}">${browGlyph(state, side)}</span>`;
  const cur = tile.querySelector(".tile-eyes");
  if (cur) cur.outerHTML = span;
  else tile.insertAdjacentHTML("beforeend", span);
}

// ---- transforms (reel poses) -------------------------------------------------------------------
function poseTo(tile: HTMLElement, transform: string, ms: number): void {
  tile.style.transition = `transform ${ms}ms cubic-bezier(0.34, 1.4, 0.5, 1)`;
  tile.style.transform = transform;
}
function rest(tile: HTMLElement): void {
  if (document.body.contains(tile)) tile.style.transform = "";
}

// ---- love / troll particles (small, no emoji) --------------------------------------------------
function particle(kind: "love" | "troll", px: number): HTMLElement {
  const el = document.createElement("div");
  el.className = `p-particle p-particle-${kind}`;
  el.style.width = `${px}px`;
  el.style.height = `${px}px`;
  el.innerHTML =
    kind === "love"
      ? `<svg viewBox="0 0 16 14"><path d="M8 13 C2 8 2 3 5 3 C7 3 8 5 8 5 C8 5 9 3 11 3 C14 3 14 8 8 13 Z"/></svg>`
      : `<svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="6"/></svg>`;
  return el;
}

/** Fire a small burst of particles from one tile toward another over `travel` ms (captured by the
 *  reel at start, so a mid-reel replay-speed change can't desync the particles from the react beat). */
function fire(from: HTMLElement, to: HTMLElement, kind: "love" | "troll", travel: number): void {
  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  const x0 = a.left + a.width / 2;
  const y0 = a.top + a.height / 2;
  const dx = b.left - a.left;
  const dy = b.top - a.top;
  const px = 11 * feel.personality.particleScale * (bigMode ? 1.9 : 1);
  const count = Math.round(feel.personality.particleCount * (bigMode ? 2.2 : 1));
  for (let i = 0; i < count; i++) {
    const el = particle(kind, px);
    el.style.left = `${x0}px`;
    el.style.top = `${y0}px`;
    document.body.appendChild(el);
    const jx = (rand() - 0.5) * 22;
    const jy = (rand() - 0.5) * 22;
    window.setTimeout(() => {
      el.style.transition = `transform ${travel}ms ease-out, opacity ${travel}ms ease-in`;
      el.style.transform = `translate(${dx + jx}px, ${dy + jy}px)`;
      el.style.opacity = "0";
    }, i * 40);
    window.setTimeout(() => el.remove(), travel + i * 40 + 120);
  }
}

/** Sender anticipation: a little jiggle (CSS keyframe) around the standing pose during wind-up. */
function windup(tile: HTMLElement, ms: number): void {
  tile.style.transition = "";
  tile.style.animation = `p-windup ${ms}ms ease-in-out`;
}
function clearWindup(tile: HTMLElement, holdTransform: string): void {
  tile.style.animation = "";
  tile.style.transform = holdTransform;
}

// ---- the reel: sender leads, responder gets ready and both pay off ------------------------------
function reel(sender: HTMLElement, receiver: HTMLElement, sSide: "left" | "right", flirting: boolean): void {
  const P = feel.personality;
  const rm = reduceMotion(); // keep the brow story, skip the poses / jiggle / flying particles
  const bs = bigMode ? 1.7 : 1; // "bigger personality": exaggerate the poses in big mode
  const up = (px: number) => -px * bs; // upward rise, amplified
  const pop = (amt: number) => 1 + amt * bs; // scale punch, amplified
  const move = (t: HTMLElement, transform: string, ms: number) => {
    if (!rm) poseTo(t, transform, ms);
  };
  const s = speed(); // every beat below is divided by the overall personality speed
  const stand = P.standMs / s, wind = P.windupMs / s, travel = P.travelMs / s;
  const react = P.reactMs / s, sit = P.sitMs / s, resolve = P.resolveMs / s;
  const rSide = sSide === "left" ? "right" : "left";
  const tilt = sSide === "left" ? 1 : -1; // rotation that tilts the sender's top toward the receiver
  const accent = flirting ? "flirt" : "troll";
  const alive = (t: HTMLElement) => document.body.contains(t);
  const at = (ms: number, fn: () => void) => window.setTimeout(fn, ms);

  // 1 — STAND: sender rises, engaged
  if (alive(sender)) {
    setBrows(sender, "engaged", sSide);
    move(sender, `translateY(${up(14)}px)`, stand);
  }
  // 2 — WIND-UP: sender jiggles (anticipation) — AND (3) the responder stands up ready to receive
  at(stand, () => {
    if (alive(sender)) {
      setBrows(sender, "toward", sSide, accent);
      if (!rm) windup(sender, wind);
    }
    if (alive(receiver)) {
      setBrows(receiver, "toward", rSide, accent); // looks toward the sender, ready
      move(receiver, `translateY(${up(12)}px)`, wind);
    }
  });
  // 4 — FIRE: the particle is launched
  const fireAt = stand + wind;
  at(fireAt, () => {
    if (alive(sender) && !rm) clearWindup(sender, `translateY(${up(14)}px) rotate(${tilt * 9}deg)`);
    if (alive(sender) && alive(receiver) && !rm) fire(sender, receiver, flirting ? "love" : "troll", travel);
  });
  // 5 — REACT: after travel, BOTH do the final react
  const reactAt = fireAt + travel;
  at(reactAt, () => {
    if (alive(sender)) {
      setBrows(sender, flirting ? "wink" : "toward", sSide, accent); // cheeky wink on a landed flirt
      move(sender, `translateY(${up(15)}px) scale(${pop(0.06)})`, react);
    }
    if (alive(receiver)) {
      if (flirting) {
        setBrows(receiver, "engaged", rSide, "flirt");
        move(receiver, `translateY(${up(17)}px) scale(${pop(0.09)})`, react); // delighted
      } else {
        setBrows(receiver, "worried", rSide, "troll");
        move(receiver, `translateY(${6 * bs}px) rotate(${-tilt * 11}deg) scale(0.9)`, react); // recoil
      }
    }
  });
  // 6 — SIT: both settle back down
  const sitAt = reactAt + react;
  at(sitAt, () => {
    [sender, receiver].forEach((t) => {
      if (alive(t)) move(t, ``, sit);
    });
  });
  // 7 — RESOLVE: brows resolve, then transition to the idle (dim) state
  const resolveAt = sitAt + sit;
  at(resolveAt, () => {
    ([[sender, sSide], [receiver, rSide]] as const).forEach(([t, side]) => {
      if (!alive(t)) return;
      setBrows(t, "dim", side);
      window.setTimeout(() => rest(t), resolve);
    });
  });
}

// ---- per-tile dressing + single-tile emote -----------------------------------------------------
function dress(tile: HTMLElement, r: Rarity, side: "left" | "right"): void {
  tile.classList.remove("p-uncommon", "p-rare", "p-epic");
  tile.querySelector(".tile-eyes")?.remove();
  tile.querySelectorAll(".tile-limb").forEach((e) => e.remove());
  delete tile.dataset.pside;
  if (r === 0) {
    tile.style.removeProperty("--p-hue");
    return;
  }
  tile.style.setProperty("--p-hue", String(hueOf(tile.textContent || "a")));
  tile.classList.add(r === 1 ? "p-uncommon" : r === 2 ? "p-rare" : "p-epic");
  if (r >= 2) {
    tile.dataset.pside = side; // remembered for idle gestures
    setBrows(tile, "dim", side); // resting brows land with the letter
    setLimbs(tile, pStyle === "limbs");
  }
}

function place(tiles: HTMLElement[]): Rarity[] {
  const I = intensity(feel.personality);
  const activate = Math.min(1, feel.personality.activation * I);
  const mid = (tiles.length - 1) / 2;
  return tiles.map((tile, i) => {
    const base = rarityOf(tile.textContent || "");
    const r: Rarity = base > 0 && rand() < activate ? base : 0;
    dress(tile, r, i <= mid ? "left" : "right");
    return r;
  });
}

function react(tiles: HTMLElement[], rarities: Rarity[], mood: "excited" | "worried"): void {
  const I = intensity(feel.personality);
  const emote = bigMode ? 1 : Math.min(1, feel.personality.emoteChance * I);
  const flirt = bigMode ? 0.9 : Math.min(1, feel.personality.flirtChance * I);
  const mid = (tiles.length - 1) / 2;

  const rares: Array<{ tile: HTMLElement; side: "left" | "right"; r: Rarity }> = [];
  tiles.forEach((tile, i) => {
    if (rarities[i] < 2 || !document.body.contains(tile)) return;
    rares.push({ tile, side: i <= mid ? "left" : "right", r: rarities[i] });
  });

  // Decide the reel FIRST so its pair is excluded from the plain single-tile emote.
  const armed = showcaseArmed; // consume the showcase arming every react
  const forcedFlirt = showcaseFlirt;
  showcaseArmed = false;
  const minR = armed ? showcaseMinRarity : 3; // normal reel needs epics; welcome allows rares
  const pool = rares.filter((x) => x.r >= minR);
  const reelPair =
    mood === "excited" && pool.length >= 2 && (armed || rand() < flirt) ? [pool[0], pool[1]] : null;

  for (const x of rares) {
    if (reelPair && (x.tile === reelPair[0].tile || x.tile === reelPair[1].tile)) continue;
    if (mood === "worried") setBrows(x.tile, "worried", x.side);
    else if (rand() < emote) {
      setBrows(x.tile, "engaged", x.side);
      window.setTimeout(() => {
        if (document.body.contains(x.tile) && x.tile.querySelector(".tile-eyes"))
          setBrows(x.tile, "dim", x.side);
      }, feel.personality.holdMs / speed());
    }
  }

  if (reelPair) {
    const flirting = armed ? forcedFlirt ?? rand() < 0.5 : rand() < 0.5;
    reel(reelPair[0].tile, reelPair[1].tile, reelPair[0].side, flirting);
  }
}

function run(tiles: HTMLElement[], mood: "excited" | "worried"): void {
  lastActivity = performance.now(); // idle life stays quiet for a beat after a real reaction
  const rarities = place(tiles); // skins + resting brows land WITH the letters
  window.setTimeout(() => react(tiles, rarities, mood), feel.personality.reactDelayMs); // then react
}

export function registerPersonality(): void {
  registerEffect({ name: "personality", label: "9 · Personality" }, "word:valid", ({ tiles }) => {
    run(tiles, "excited");
  });
  bus.on("word:invalid", ({ tiles }) => {
    if (effectEnabled("personality")) run(tiles, "worried");
  });
  startIdle(); // occasional silly gestures on resting tiles
}
